import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Info, Minus } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useChart, useChartDates, useStations } from "../api";
import { ArtistLinks } from "../components/ArtistLinks";
import { DatePicker } from "../components/DatePicker";
import { StationLogo } from "../components/StationLogo";
import { Cover } from "../components/Cover";
import { ErrorBox, Loading } from "../components/State";
import { longDate, movement } from "../format";
import { useTitle } from "../hooks";

export function ChartView() {
  const { chart = "", date = "latest" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const highlight = Number(params.get("utwor")) || null; // arrived from a track page: mark that song
  const stations = useStations();
  const { data, isLoading, error } = useChart(chart, date);
  const dates = useChartDates(chart, true);
  useTitle(data ? `${data.chart.name} · ${longDate(data.date)}` : undefined);

  // Asked for a day without a chart: the API returned the nearest one, so move the URL there and say so.
  const adjustedFrom = (location.state as { from?: string } | null)?.from;
  useEffect(() => {
    if (data && date !== "latest" && data.date !== date)
      navigate(`/listy/${chart}/${data.date}${location.search}`, {
        replace: true,
        state: { from: date },
      });
  }, [data, date, chart, navigate, location.search]);

  useEffect(() => {
    if (data && highlight) document.querySelector(".chart__row.is-hit")?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [data, highlight]);

  // ← / → move between charts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        !data ||
        e.altKey ||
        e.ctrlKey ||
        e.metaKey ||
        /input|select|textarea/i.test((e.target as HTMLElement).tagName)
      )
        return;
      if (e.key === "ArrowLeft" && data.prev)
        navigate(`/listy/${chart}/${data.prev}`);
      if (e.key === "ArrowRight" && data.next)
        navigate(`/listy/${chart}/${data.next}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [data, chart, navigate]);

  const info = stations.data?.stations
    .flatMap((s) => s.charts)
    .find((c) => c.slug === chart);

  return (
    <>
      <div className="chart-head">
        <div className="chart-head__title">
          {data && <StationLogo slug={data.chart.station.slug} size={56} />}
          <div>
            <p className="kicker">{data?.chart.station.name ?? " "}</p>
            <h1>{data?.chart.name ?? "Lista"}</h1>
          </div>
        </div>
        <label className="chart-pick">
          Zmień listę
          <select
            value={chart}
            onChange={(e) => navigate(`/listy/${e.target.value}`)}
          >
            {stations.data?.stations.flatMap((s) =>
              s.charts.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {s.name} {c.name}
                </option>
              )),
            )}
          </select>
        </label>
      </div>

      {error ? <ErrorBox error={error} /> : null}
      {isLoading && <Loading />}
      {data && (
        <>
          <nav className="datenav" aria-label="Nawigacja po datach">
            {data.prev ? (
              <Link
                className="btn"
                to={`/listy/${chart}/${data.prev}`}
                rel="prev"
              >
                <ChevronLeft size={18} aria-hidden="true" /> {longDate(data.prev)}
              </Link>
            ) : (
              <span className="btn is-disabled"><ChevronLeft size={18} aria-hidden="true" /> początek</span>
            )}
            <div className="datenav__now">
              <DatePicker
                label="Wybierz dzień"
                value={data.date}
                dates={dates.data?.dates}
                onChange={(d) => navigate(`/listy/${chart}/${d}`)}
              />
            </div>
            {data.next ? (
              <Link
                className="btn"
                to={`/listy/${chart}/${data.next}`}
                rel="next"
              >
                {longDate(data.next)} <ChevronRight size={18} aria-hidden="true" />
              </Link>
            ) : (
              <span className="btn is-disabled">najnowsza <ChevronRight size={18} aria-hidden="true" /></span>
            )}
          </nav>
          {adjustedFrom && (
            <p className="notice">
              <Info size={18} aria-hidden="true" />
              <span>Nie było notowania {longDate(adjustedFrom)}, oto najbliższe.</span>
            </p>
          )}

          <ol className="chart">
            {data.entries.map((e, i) => {
              const m = movement(e.position, e.prevPosition, e.weeksOnChart);
              const tied = data.entries[i - 1]?.position === e.position;
              return (
                <li
                  key={`${e.position}-${e.track.id}`}
                  data-rank={e.position <= 3 ? e.position : undefined}
                  className={`chart__row${e.position <= 3 ? " is-top" : ""}${e.track.id === highlight ? " is-hit" : ""}`}
                >
                  <span
                    className="chart__pos"
                    aria-label={`Miejsce ${e.position}`}
                  >
                    {tied ? "" : e.position}
                  </span>
                  <Cover
                    url={e.track.imageUrl}
                    id={e.track.id}
                    title={e.track.title}
                    play={e.track}
                  />
                  <div className="chart__info">
                    <Link className="chart__title" to={`/utwor/${e.track.id}`}>
                      {e.track.title}
                    </Link>
                    <div className="chart__artists">
                      <ArtistLinks
                        artists={e.track.artists}
                        fallback={e.track.credit}
                      />
                    </div>
                  </div>
                  <div className="chart__meta">
                    {m?.kind === "new" && (
                      <span className="move move--new">nowość</span>
                    )}
                    {m?.kind === "up" && (
                      <span
                        className="move move--up"
                        title={`Poprzednio: ${e.prevPosition}`}
                      >
                        <ArrowUp size={14} strokeWidth={3} aria-label="w górę o" /> {m.by}
                      </span>
                    )}
                    {m?.kind === "down" && (
                      <span
                        className="move move--down"
                        title={`Poprzednio: ${e.prevPosition}`}
                      >
                        <ArrowDown size={14} strokeWidth={3} aria-label="w dół o" /> {m.by}
                      </span>
                    )}
                    {m?.kind === "same" && (
                      <span className="move move--same" title="Bez zmian">
                        <Minus size={14} strokeWidth={3} aria-label="bez zmian" />
                      </span>
                    )}
                    {e.weeksOnChart ? (
                      <span className="muted small">{e.weeksOnChart} tyg.</span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
          <p className="muted small">
            {data.sourceUrl && (
              <>
                Źródło:{" "}
                <a
                  href={data.sourceUrl}
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  strona rozgłośni
                </a>
                .{" "}
              </>
            )}
            Skróty: strzałki w lewo i w prawo przechodzą między notowaniami.
          </p>
        </>
      )}
    </>
  );
}
